import AsideCard from '@/components/patterns/aside-card'
import { Button } from '@/components/ui/button'
import { useFormSection } from '@/hooks/useFormSection'
import { useIsReadOnly } from '@/lib/casl'
import { DAYS_OF_WEEK, getDefaultHours } from '@/lib/constants/businessHours'
import { useFormContext } from '@/lib/form-contexts'
// Intentional: open/close are wall-clock HH:mm strings (no absolute date/timestamp).
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { Clock } from 'lucide-react'
import { useId } from 'react'
import { Typography } from '@/components/ui/typography'
dayjs.extend(customParseFormat)

// Format time from 24h to 12h format
function formatTime(time) {
  if (!time) return ''
  return dayjs(time, 'HH:mm').format('h:mm A')
}

// Preview component showing how hours will display to customers
const HoursPreview = ({ hours, title = 'Hours' }) => {
  if (!hours || hours.length === 0) {
    return (
      <div className="text-muted-foreground text-sm">
        <Typography variant="body">No hours configured</Typography>
      </div>
    )
  }

  return (
    <div className="bg-muted/30 rounded-lg p-4 border">
      <div className="flex items-start gap-3">
        <Clock className="w-5 h-5 mt-0.5 flex-shrink-0 text-muted-foreground" />
        <div className="flex-1">
          <Typography variant="body" weight="semibold" className="mb-3">
            {title}
          </Typography>
          <div className="space-y-1.5">
            {hours.map((day) => (
              <div key={day.id || day.name} className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">{day.name}</span>
                <span className={day.is_open ? 'font-medium' : 'text-muted-foreground'}>
                  {day.is_open ? `${formatTime(day.open)} - ${formatTime(day.close)}` : 'Closed'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

const WeeklyHoursInputs = ({
  initialHours = [],
  title = '',
  subtitle = '',
  fieldName = 'open_hours',
  icon,
}) => {
  const id = useId()
  const form = useFormContext()
  const { isReadOnly } = useIsReadOnly()

  // Calculate maximum open time (1 hour before close time)
  const getMaxOpenTime = (closeTime) => {
    if (!closeTime) return '23:59'

    const [hours, minutes] = closeTime.split(':')
    const closeHour = parseInt(hours)
    const closeMinute = parseInt(minutes)

    // Subtract 1 hour from close time
    let openHour = closeHour - 1
    let openMinute = closeMinute

    // Handle hour underflow
    if (openHour < 0) {
      openHour = 0
      openMinute = 0
    }

    return `${openHour.toString().padStart(2, '0')}:${openMinute.toString().padStart(2, '0')}`
  }

  // Calculate minimum close time (1 hour after open time)
  const getMinCloseTime = (openTime) => {
    if (!openTime) return '00:00'

    const [hours, minutes] = openTime.split(':')
    const openHour = parseInt(hours)
    const openMinute = parseInt(minutes)

    // Add 1 hour to open time
    let closeHour = openHour + 1
    let closeMinute = openMinute

    // Handle hour overflow
    if (closeHour >= 24) {
      closeHour = 23
      closeMinute = 59
    }

    return `${closeHour.toString().padStart(2, '0')}:${closeMinute.toString().padStart(2, '0')}`
  }

  const transformHoursForForm = (hours) => {
    if (!hours || hours.length === 0) {
      return getDefaultHours()
    }

    // Ensure we have all 7 days, filling in missing ones
    const completeHours = DAYS_OF_WEEK.map((day) => {
      const existingDay = hours.find((h) => h.id === day.id)
      return (
        existingDay || {
          id: day.id,
          name: day.name,
          open: '09:00',
          close: '17:00',
          is_open: true,
        }
      )
    })

    return completeHours
  }

  // Use the form section hook to manage this section's data
  const { data: hoursData, reset } = useFormSection({
    fieldName,
    initialData: initialHours,
    getDefaultData: getDefaultHours,
    transformData: transformHoursForForm,
  })

  return (
    <AsideCard
      title={title || null}
      description={subtitle || null}
      icon={icon}
      action={
        !isReadOnly ? (
          <Button variant="outline" size="sm" onClick={reset}>
            Reset To Defaults
          </Button>
        ) : null
      }
      asideTitle="Customer Preview"
      aside={
        <form.Subscribe selector={(state) => state.values[fieldName]}>
          {(hours) => <HoursPreview hours={hours || hoursData} title={title} />}
        </form.Subscribe>
      }
    >
      <div className="space-y-4">
        <form.AppField name={fieldName} mode="array">
          {(field) => (
            <>
              {(field.state.value || hoursData).map((day, index) => (
                <div key={day.id}>
                  <form.AppField name={`${fieldName}.${index}.is_open`}>
                    {(isOpenField) => (
                      <div
                        className={`grid grid-cols-2 gap-2 place-items-center px-4 py-3 min-h-[66px] rounded-lg border ${
                          isOpenField.state.value
                            ? 'bg-background/50 dark:bg-muted'
                            : 'bg-muted dark:bg-muted/50'
                        }`}
                      >
                        <div className="flex items-center space-x-4 col-span-2 sm:col-span-1">
                          <div className="w-20">
                            <Typography variant="body" weight="medium">
                              {day.name}
                            </Typography>
                          </div>

                          <div className="flex items-center space-x-2">
                            <isOpenField.SwitchInput
                              id={`${id}-hours-${index}-is_open`}
                              label={isOpenField.state.value ? 'Open' : 'Closed'}
                            />
                          </div>
                        </div>

                        {isOpenField.state.value && (
                          <div className="flex items-center space-x-2 col-span-2 sm:col-span-1">
                            <div className="flex items-center space-x-1">
                              <form.Subscribe
                                selector={(state) => state.values[fieldName]?.[index]?.close}
                              >
                                {(closeTime) => (
                                  <form.AppField name={`${fieldName}.${index}.open`}>
                                    {(openField) => (
                                      <openField.HourInput
                                        id={`${id}-hours-${index}-open`}
                                        minHour="00:00"
                                        maxHour={getMaxOpenTime(closeTime)}
                                      />
                                    )}
                                  </form.AppField>
                                )}
                              </form.Subscribe>
                              <Typography variant="label" tone="muted">
                                -
                              </Typography>
                              <form.Subscribe
                                selector={(state) => state.values[fieldName]?.[index]?.open}
                              >
                                {(openTime) => (
                                  <form.AppField name={`${fieldName}.${index}.close`}>
                                    {(closeField) => (
                                      <closeField.HourInput
                                        id={`${id}-hours-${index}-close`}
                                        minHour={getMinCloseTime(openTime)}
                                        maxHour="23:59"
                                      />
                                    )}
                                  </form.AppField>
                                )}
                              </form.Subscribe>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </form.AppField>
                </div>
              ))}
            </>
          )}
        </form.AppField>
      </div>
    </AsideCard>
  )
}

export default WeeklyHoursInputs
